/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { useControlledState } from '@react-stately/utils';
import * as stylex from '@stylexjs/stylex';
import type { HTMLAttributes } from 'react';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import { type ContextValue, Provider, useContextProps } from 'react-aria-components';
import { breakpoints } from '../../constants.stylex';
import { $ } from '../../tokens.stylex';
import { EmptyStateContext } from '../empty-state/empty-state.component';
import { ListContext } from '../list/list.component';
import { ChatMessageBarContext } from '../chat-message-bar/chat-message-bar.component';

interface ChatWindowProps extends Omit<HTMLAttributes<HTMLElement>, 'style' | 'className'> {
  /** Whether the chat window is expanded. (Controlled) */
  isOpen?: boolean;
  /** Whether the chat window is expanded by default. (Uncontrolled) */
  defaultOpen?: boolean;
  /** Called when the expanded state changes. */
  onOpenChange?: (isOpen: boolean) => void;
  /** Whether to disable closing the chat window with the Escape key. (Default: false) */
  isKeyboardDismissDisabled?: boolean;
  /** The horizontal position of the chat window. (Default: right) */
  position?: keyof typeof positionVariants;
  style?: stylex.StyleXStyles;
  children?: React.ReactNode;
}

interface ChatWindowState {
  expanded: boolean;
  setExpanded: (value: boolean) => void;
}

const ChatWindowContext = createContext<ContextValue<ChatWindowProps, HTMLElement>>({});
const ChatWindowStateContext = createContext<ChatWindowState>({ expanded: true, setExpanded: () => void 0 });

const ChatWindow = forwardRef((props: ChatWindowProps, ref: ForwardedRef<HTMLElement>) => {
  [props, ref] = useContextProps(props, ref, ChatWindowContext);

  const {
    style,
    children,
    isOpen,
    defaultOpen = false,
    onOpenChange,
    isKeyboardDismissDisabled = false,
    position = 'right',
    ...coreProps
  } = props;

  const [expanded, setExpanded] = useControlledState(isOpen, defaultOpen, onOpenChange);

  return (
    <section
      ref={ref}
      aria-label="Chat window"
      aria-expanded={expanded}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && expanded && !isKeyboardDismissDisabled) {
          setExpanded(false);
        }
      }}
      {...stylex.props(styles.base, positionVariants[position], expanded ? styles.expanded : styles.collapsed, style)}
      {...coreProps}>
      <Provider
        values={[
          [
            ListContext,
            {
              inert: !expanded,
              isOrdered: true,
              role: 'log',
              style: listStyles.base,
            },
          ],
          [
            ChatWindowStateContext,
            {
              expanded,
              setExpanded,
            },
          ],
          [
            ChatMessageBarContext,
            {
              inert: !expanded,
            }
          ],
          [
            EmptyStateContext,
            {
              style: emptyStateStyles.base
            }
          ]
        ]}>
        {children}
      </Provider>
    </section>
  );
});

const styles = stylex.create({
  base: {
    borderColor: $.border08,
    borderStyle: 'solid',
    borderWidth: 1,
    outline: 'none',
    overflow: 'hidden',
    backgroundColor: $.surfaceBGAlternative01,
    boxShadow: $.shadowBottom060Medium,
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    transitionDuration: '300ms',
    transitionProperty: 'transform',
    transitionTimingFunction: 'ease-in-out',
    zIndex: 100,
    borderBottomStyle: 'none',
    borderTopLeftRadius: {
      default: 10,
      [breakpoints.small]: 0,
    },
    borderTopRightRadius: {
      default: 10,
      [breakpoints.small]: 0,
    },
    bottom: 0,
    // Full height is always reserved so content can be pre-scrolled before/during animation
    height: {
      default: 513,
      [breakpoints.small]: '100dvh',
    },
    maxHeight: '100dvh',
    maxWidth: '100dvw',
    width: {
      default: 390,
      [breakpoints.small]: '100%',
    },
  },
  expanded: {
    transform: 'translateY(0)',
  },
  collapsed: {
    // Slide the panel down so only the 56px header remains visible above the viewport bottom.
    // calc(100% - 56px) is relative to the element's own height, so it works at both
    // 513px (desktop) and 100dvh (mobile) without animating when the breakpoint changes.
    transform: 'translateY(calc(100% - 56px))',
  },
});

const positionVariants = stylex.create({
  left: {
    left: {
      default: 16,
      [breakpoints.small]: 0,
    },
    right: 'auto',
  },
  center: {
    transform: {
      default: 'translateX(-50%)',
      [breakpoints.small]: 'none',
    },
    left: {
      default: '50%',
      [breakpoints.small]: 0,
    },
    right: {
      default: 'auto',
      [breakpoints.small]: 0,
    },
  },
  right: {
    left: 'auto',
    right: {
      default: 16,
      [breakpoints.small]: 0,
    },
  },
});

const listStyles = stylex.create({
  base: {
    gap: 12,
    marginBlock: 8,
    marginInline: 16,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    scrollbarWidth: 'none',
    overflowY: 'auto',
  },
});

const emptyStateStyles = stylex.create({
  base: {
    height: '100%',
  }
});

export { ChatWindow, ChatWindowContext, ChatWindowStateContext, type ChatWindowProps, type ChatWindowState };
